import { useLocation, useNavigate, useParams } from "react-router-dom";
import { infoIcon, formBg } from "../../asset";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import CustomSelect from "../../components/CustomSelect";
import PageHeader from "../../components/PageHeader";
import { useEffect, useState } from "react";
import examsApis from "../../api/ExamsApis";
import { CreateExamPayload } from "../../types/ExamType";
import { useBranchCode } from "../../util/hooks/useBranch";
import SelectClassModal from "../../components/modal/SelectClassModel";

const initialState = {
  examName: "",
  classId: "",
  // subjectId: "",
  totalMarks: "",
  passMarks: "",
  defaultMarksPerQuestion: "",
  duration: "",
  startDate: "",
};

export default function CreateExam() {
  const [formData, setFormData] = useState(initialState);
  const navigate = useNavigate();
  const { examId } = useParams<{ examId: string }>();
  const { state } = useLocation();
  const examSeqId = state?.examSeqId;
  const isEdit = Boolean(examId);
  const branchCode = useBranchCode();
  const [activePopup, setActivePopup] = useState<"class" | null>(null);

  useEffect(() => {
    if (!examSeqId) return;

    examsApis.getExamsBySeqId(examSeqId).then((res) => {
      const exam = res?.data;
      if (!exam) return;

      setFormData({
        examName: exam.examName,
        totalMarks: exam.totalMarks,
        passMarks: exam.passMarks,
        defaultMarksPerQuestion: exam.defaultMarksPerQuestion,
        duration: exam.duration?.toString(),
        startDate: exam.startDate?.slice(0, 10),
        classId: exam.classId?.toString(),
      });
    });
  }, [examSeqId]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload: CreateExamPayload = {
      ...formData,
      duration: formData.duration ? +formData.duration : undefined,
      startDate: new Date(formData.startDate).toISOString(),
    };

    if (isEdit) {
      const res = await examsApis.updateExam(examId, payload);
      if (res?.status === 1) {
        navigate(`/exams-and-assignments/create-exam/test/${res.data.examId}`);
      }
    } else {
      if (!branchCode) return;
      const res = await examsApis.createExam(branchCode, payload);
      if (res?.status === 1) {
        navigate(`/exams-and-assignments/create-exam/test/${res.data.examId}`);
      }
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <PageHeader title={isEdit ? examSeqId : "CREATE NEW EXAM"} showBack />

      <div
        className="bg-white rounded-lg p-5 gap-7 flex flex-col justify-center items-center bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${formBg})` }}
      >
        <div className="flex flex-col gap-2 justify-center items-center">
          <h3 className="text-sm font-semibold text-neutral-300">
            Exam Management & Setup
          </h3>
          <p className="text-xl md:text-3xl font-semibold text-primary-600">
            Organize, schedule, and configure exams effortlessly
          </p>
        </div>

        <form
          className="w-full max-w-3xl mx-auto flex flex-col gap-8"
          onSubmit={handleSubmit}
        >
          <CustomInput
            name="examName"
            value={formData.examName}
            onChange={handleChange}
            placeholder="Eg: Unit Test 1"
            label="Exam Name"
          />

          <div className="flex flex-col md:flex-row w-full gap-6">
            <CustomSelect
              name="classId"
              value={formData.classId}
              onClick={() => setActivePopup("class")}
              onChange={handleChange}
              placeholder="Select Class"
              label="Class"
              options={[]}
              openAsPopup
            />

            {activePopup === "class" && (
              <SelectClassModal
                title="Select Class"
                actionLabel="Confirm"
                onClose={() => setActivePopup(null)}
                onAction={(selectedClasses) => {
                  setFormData((prev) => ({
                    ...prev,
                    selectedClasses
                  }));
                  setActivePopup(null);
                }}
              />
            )}

            <div className="md:w-[70%]">
              <CustomSelect
                name="examName"
                value={formData.examName}
                onChange={handleChange}
                placeholder="Select Subject"
                label="Subject"
                options={[]}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 w-full gap-6">
            <CustomInput
              name="totalMarks"
              value={formData.totalMarks}
              onChange={handleChange}
              placeholder="Eg: 100"
              label="Total Marks"
            />

            <CustomInput
              name="passMarks"
              value={formData.passMarks}
              onChange={handleChange}
              placeholder="Eg: 60"
              label="Pass Marks"
              className="w-full"
            />
          </div>

          <div className="grid md:grid-cols-2 w-full gap-6">
            <CustomInput
              name="defaultMarksPerQuestion"
              value={formData.defaultMarksPerQuestion}
              onChange={handleChange}
              placeholder="Enter the default marks"
              label="Default Marks per Question"
            />

            <div className="relative w-full">
              <div className="flex items-center gap-2">
                <CustomSelect
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  placeholder="Eg: 60 mins"
                  label="Set Duration"
                  className="w-full"
                  options={["30", "60"]}
                />

                <div className="absolute top-0 md:left-[28%] hidden md:block lg:left-[25%] group cursor-pointer">
                  <img src={infoIcon} alt="info" className="w-3 h-3" />
                  <div className="absolute bottom-5 hidden group-hover:block w-80 bg-white text-error-600 text-xs p-4 rounded-xl shadow-lg z-10">
                    The exam will automatically end based on the duration you
                    enter. Leave it blank if you don’t want a time limit.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <CustomInput
            name="startDate"
            type="date"
            value={formData.startDate}
            onChange={handleChange}
            label="Start Date"
          />

          <div className="flex justify-end items-end">
            <CustomButton color="bg-primary-600" text="Next" type="submit" />
          </div>
        </form>
      </div>
    </div>
  );
}
